
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsLayout } from '../../components/SettingsLayout';
import { Bolt, WorkspacePremium, Add, PictureAsPdf } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

export const BillingScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <SettingsLayout>
      <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="mb-10">
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{t('billing.title')}</h1>
            <p className="text-slate-500 mt-2 text-base">{t('billing.subtitle')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-10 space-y-12">
                <div className="flex items-center justify-between p-8 bg-blue-50/50 rounded-2xl border border-blue-100">
                    <div className="flex items-center gap-6">
                        <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center flex-shrink-0 border border-blue-100">
                            <Bolt className="text-primary" style={{ fontSize: 32 }} />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-blue-600 uppercase tracking-wider">{t('billing.currentBalance')}</p>
                            <h3 className="text-3xl font-bold text-slate-900 mt-1">1,250 <span className="text-lg font-semibold text-slate-500 ml-1">{t('billing.credits')}</span></h3>
                            <p className="text-xs text-slate-500 mt-2">{t('billing.balanceHint')}</p>
                        </div>
                    </div>
                    <button
                      onClick={() => navigate('/settings/credits')}
                      className="px-8 py-3 bg-primary text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-all shadow-md shadow-blue-100 hover:shadow-lg hover:shadow-blue-200 flex items-center gap-2 active:scale-95"
                    >
                        <Add fontSize="small" />
                        {t('billing.topUp')}
                    </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
                    <div className="border border-slate-200 rounded-xl p-6 bg-white">
                        <h3 className="text-lg font-bold text-slate-900 mb-4">{t('billing.pricingTitle')}</h3>
                        <ul className="space-y-2 text-sm text-slate-600">
                            <li>{t('billing.ruleImagePrice')}</li>
                            <li>{t('billing.ruleExchange')}</li>
                            <li>{t('billing.ruleRollover')}</li>
                            <li>{t('billing.ruleGift')}</li>
                        </ul>
                    </div>
                    <div className="border border-slate-200 rounded-xl p-6 bg-white">
                        <h3 className="text-lg font-bold text-slate-900 mb-4">{t('billing.faqTitle')}</h3>
                        <div className="space-y-4 text-sm text-slate-600">
                            <div>
                                <p className="font-semibold text-slate-900">{t('billing.faq.q1')}</p>
                                <p className="mt-1">{t('billing.faq.a1')}</p>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900">{t('billing.faq.q2')}</p>
                                <p className="mt-1">{t('billing.faq.a2')}</p>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900">{t('billing.faq.q3')}</p>
                                <p className="mt-1">{t('billing.faq.a3')}</p>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900">{t('billing.faq.q4')}</p>
                                <p className="mt-1">{t('billing.faq.a4')}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pt-2">
                    <h3 className="text-lg font-bold text-slate-900 mb-6">{t('billing.subscriptionPlan')}</h3>
                    <div className="flex items-center justify-between p-6 bg-[#F8FAFC] border border-slate-200 rounded-xl">
                        <div className="flex items-center gap-5">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border border-slate-100">
                                <WorkspacePremium className="text-slate-600" />
                            </div>
                            <div>
                                <h4 className="text-base font-bold text-slate-900">{t('billing.proPlanPrice')}</h4>
                                <p className="text-sm text-slate-500 mt-0.5 font-medium">{t('billing.nextRenewal')}</p>
                            </div>
                        </div>
                        <button
                          onClick={() => navigate('/settings/subscription')}
                          className="text-sm font-bold text-primary hover:text-blue-700 transition-colors px-4 py-2 border border-blue-100 bg-white rounded-lg hover:bg-blue-50 active:scale-95"
                        >
                          {t('billing.managePlan')}
                        </button>
                    </div>
                </div>
                <div className="pt-4">
                    <h3 className="text-lg font-bold text-slate-900 mb-6">{t('billing.billingHistory')}</h3>
                    <div className="overflow-hidden border border-slate-200 rounded-xl">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">{t('billing.date')}</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">{t('billing.description')}</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">{t('billing.amount')}</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest text-right">{t('billing.invoice')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                <tr className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-5 text-sm font-medium text-slate-600">Jan 24, 2026</td>
                                    <td className="px-6 py-5 text-sm font-semibold text-slate-900">{t('billing.proSubscription')}</td>
                                    <td className="px-6 py-5 text-sm font-semibold text-slate-900">$49.00</td>
                                    <td className="px-6 py-5 text-right">
                                        <button className="text-slate-400 hover:text-primary transition-colors p-2 rounded-full hover:bg-slate-100 active:scale-90">
                                            <PictureAsPdf />
                                        </button>
                                    </td>
                                </tr>
                                <tr className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-5 text-sm font-medium text-slate-600">Jan 15, 2026</td>
                                    <td className="px-6 py-5 text-sm font-semibold text-slate-900">{t('billing.creditTopUp')}</td>
                                    <td className="px-6 py-5 text-sm font-semibold text-slate-900">$27.00</td>
                                    <td className="px-6 py-5 text-right">
                                        <button className="text-slate-400 hover:text-primary transition-colors p-2 rounded-full hover:bg-slate-100 active:scale-90">
                                            <PictureAsPdf />
                                        </button>
                                    </td>
                                </tr>
                                <tr className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-5 text-sm font-medium text-slate-600">Dec 24, 2025</td>
                                    <td className="px-6 py-5 text-sm font-semibold text-slate-900">{t('billing.proSubscription')}</td>
                                    <td className="px-6 py-5 text-sm font-semibold text-slate-900">$49.00</td>
                                    <td className="px-6 py-5 text-right">
                                        <button className="text-slate-400 hover:text-primary transition-colors p-2 rounded-full hover:bg-slate-100 active:scale-90">
                                            <PictureAsPdf />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="bg-slate-50/50 px-10 py-6 border-t border-slate-200 flex items-center justify-between">
                <p className="text-xs text-slate-400 font-medium">{t('billing.footer')}</p>
                <div className="flex gap-6">
                    <button
                        onClick={() => alert('Downloading all invoices...')}
                        className="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-all hover:underline active:scale-95"
                    >
                        {t('billing.downloadAll')}
                    </button>
                </div>
            </div>
        </div>
        <footer className="mt-12 text-center pb-12">
            <p className="text-slate-400 text-xs font-medium uppercase tracking-[0.1em]">{t('appName')} {t('billing.footerCopyright')}</p>
        </footer>
      </div>
    </SettingsLayout>
  );
};
